// Bong bóng chat nổi ở portal /booking/me — chỉ render khi khách đã đăng
// nhập (cookie customer_session). Gửi tin qua REST, nhận realtime qua WS;
// refetchInterval là lưới an toàn khi WS rớt.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, X } from "lucide-react";
import { Button } from "@/ui/button";
import {
  appendMessage,
  customerChatAttachmentUrl,
  customerChatWsUrl,
  fetchCustomerChatMessages,
  fetchCustomerChatThreads,
  markCustomerChatRead,
  sendCustomerChatImage,
  sendCustomerChatMessage,
  type ChatCustomerThread,
  type ChatMessage,
  type ChatThreadRef,
  type ChatTopic,
} from "./api";
import { subscribeChatBubble } from "./chatBubbleBus";
import { ChatComposer } from "./ChatComposer";
import { ChatThread } from "./ChatThread";
import { useChatSocket } from "./useChatSocket";

const THREADS_KEY = ["customer-chat", "threads"] as const;
/**
 * Kho tin nhắn tách theo TỪNG luồng. Luồng chưa tồn tại chưa có mã phiên nên
 * lấy mã đơn làm khoá; không có gì thì là luồng "Hỏi chung".
 */
const messagesKey = (thread: ChatThreadRef) =>
  ["customer-chat", "messages", thread.threadId ?? thread.orderId ?? "general"] as const;

export function CustomerChatBubble() {
  const [open, setOpen] = useState(false);
  // Chủ đề áp cho tin SẮP gửi. Mặc định "general"; thẻ Giới thiệu ở cổng khách
  // mở bong bóng kèm "affiliate" để tin rơi đúng hòm của kế toán.
  const [topic, setTopic] = useState<ChatTopic>("general");
  // Luồng khách đang xem. Rỗng = "Hỏi chung" — cũng là mặc định, nên khách chưa
  // mua gì vẫn nhắn được y như trước (quyết định 0065 mục 1).
  const [thread, setThread] = useState<ChatThreadRef>({});
  const queryClient = useQueryClient();

  useEffect(
    () =>
      subscribeChatBubble((next) => {
        setTopic(next);
        setOpen(true);
      }),
    [],
  );

  const threadsQ = useQuery({
    queryKey: THREADS_KEY,
    queryFn: ({ signal }) => fetchCustomerChatThreads({ signal }),
    enabled: open,
    refetchInterval: 30_000,
  });
  const allThreads = threadsQ.data ?? [];
  // Hộp "Hỏi chung" riêng của một thương hiệu (QĐ 0310) chỉ Cổng dịch vụ mở được:
  // bong bóng này chỉ biết hộp chung mặc định, bày thêm hộp đó là hai ô "Hỏi chung".
  const threads = allThreads.filter((t) => t.orderId || !t.brandCode);
  const current = findThread(threads, thread);

  const messagesQ = useQuery({
    queryKey: messagesKey(thread),
    queryFn: ({ signal }) => fetchCustomerChatMessages({ signal, thread }),
    refetchInterval: 30_000,
  });

  useChatSocket(customerChatWsUrl(), (event) => {
    if (event.type !== "message") return;
    // Máy chủ đẩy tin của MỌI luồng khách này. Nhét nhầm tin buổi chụp A vào
    // khung buổi chụp B là đúng cái lỗi mà tính năng này sinh ra để chữa.
    //
    // Luồng chưa tồn tại (id rỗng) thì nhận những tin không thuộc luồng nào đã
    // biết — đó chính là tin đầu tiên vừa tạo ra nó.
    // Biết ĐỦ mọi luồng, kể cả hộp riêng thương hiệu đã ẩn ở trên: tin của hộp đó
    // không được rơi vào khung "Hỏi chung" mặc định chưa có mã.
    const known = new Set(allThreads.map((t) => t.id).filter(Boolean));
    const thuocKhungDangMo = current?.id
      ? event.message.sessionId === current.id
      : !known.has(event.message.sessionId);
    if (thuocKhungDangMo) {
      queryClient.setQueryData<ChatMessage[]>(messagesKey(thread), (items) =>
        appendMessage(items, event.message),
      );
    }
    // Dù tin thuộc luồng nào cũng phải làm mới danh sách: dấu chưa đọc của
    // luồng đang đóng nằm ở đó.
    void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
  });

  // Báo đã đọc khi MỞ bong bóng, và mỗi khi có tin mới trong lúc đang mở. Đóng
  // lại thì không báo — mốc "đã xem" phải ứng với lúc khách thật sự nhìn màn
  // hình. Lỗi ở đây nuốt im: khách không cần biết, và làm hỏng khung chat vì
  // một mốc "đã xem" là đánh đổi sai.
  const soTin = messagesQ.data?.length ?? 0;
  const khoaLuong = thread.threadId ?? thread.orderId ?? "";
  useEffect(() => {
    if (!open) return;
    // Ghi mốc cho ĐÚNG luồng đang mở: mở buổi chụp A không được xoá dấu chưa
    // đọc của buổi chụp B.
    void markCustomerChatRead(thread).catch(() => {});
    // thread nằm ngoài mảng phụ thuộc vì nó là object dựng mới mỗi lần vẽ;
    // khoaLuong là chuỗi, đại diện đúng cùng một thứ mà không đẻ vòng lặp.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, soTin, khoaLuong]);

  const luuTinVuaGui = (message: ChatMessage) => {
    queryClient.setQueryData<ChatMessage[]>(messagesKey(thread), (items) => appendMessage(items, message));
    // Luồng vừa được tạo bởi chính tin này → danh sách phải có mã phiên thật,
    // nếu không thì lượt gửi sau vẫn đi bằng orderId (vẫn đúng, chỉ chậm hơn).
    void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
  };

  const sendTextM = useMutation({
    mutationFn: (body: string) => sendCustomerChatMessage(body, topic, undefined, thread),
    onSuccess: luuTinVuaGui,
  });
  const sendImageM = useMutation({
    // `caption` = phần chữ khách gõ cùng lượt gửi ảnh; máy chủ gắn nó vào
    // chính tin mang ảnh nên hai thứ về thành MỘT bong bóng.
    mutationFn: (input: { file: File; caption?: string | undefined }) =>
      sendCustomerChatImage(input.file, input.caption, topic, undefined, thread),
    onSuccess: luuTinVuaGui,
  });

  if (!open) {
    return (
      <button
        type="button"
        aria-label="Chat với Gạo Nâu"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-brand)] text-white shadow-lg transition hover:scale-105"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 flex h-[min(560px,calc(100dvh-2.5rem))] w-[min(380px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-3xl border bg-surface shadow-2xl">
      <header className="flex items-center justify-between bg-[var(--color-brand)] px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Chat với Gạo Nâu</p>
          <p className="truncate text-xs text-white/95">
            {topic === "affiliate"
              ? "Mục Giới thiệu — kế toán Gạo Nâu sẽ trả lời"
              : "Chúng tôi sẽ trả lời sớm nhất có thể"}
          </p>
        </div>
        {topic === "affiliate" && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/15 hover:text-white"
            onClick={() => setTopic("general")}
          >
            Về chat thường
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Đóng chat"
          className="text-white hover:bg-white/15 hover:text-white"
          onClick={() => setOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>
      {/* Chỉ hiện khi khách CÓ buổi chụp. Khách chưa mua gì thì màn hình y hệt
          trước đây — không thêm một hàng nút vô nghĩa. */}
      {threads.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto border-b bg-surface-2/50 px-3 py-2">
          {threads.map((item) => {
            const dangChon = item === current;
            return (
              <button
                key={item.id || item.orderId || "general"}
                type="button"
                aria-pressed={dangChon}
                onClick={() => setThread(item.id ? { threadId: item.id } : { orderId: item.orderId })}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${
                  dangChon ? "border-transparent bg-[var(--color-brand)] text-white" : "hover:bg-surface-2"
                }`}
              >
                {item.orderLabel}
                {item.unreadCustomerCount > 0 && !dangChon && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-brand)] px-1 text-[10px] text-white">
                    {item.unreadCustomerCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <ChatThread
        messages={messagesQ.data ?? []}
        viewer="customer"
        attachmentUrl={(message) => (message.attachment ? customerChatAttachmentUrl(message.attachment.id) : null)}
        emptyText="Bạn cần hỗ trợ gì? Nhắn cho Gạo Nâu tại đây nhé."
      />
      <ChatComposer
        onSendText={(body) => sendTextM.mutateAsync(body)}
        onSendImage={(file, caption) => sendImageM.mutateAsync({ file, caption })}
        sending={sendTextM.isPending || sendImageM.isPending}
      />
    </div>
  );
}

/**
 * Tra luồng đang chọn. Rỗng cả hai = luồng "Hỏi chung" (luồng không gắn đơn).
 * Trả undefined khi danh sách chưa tải xong — nơi gọi phải chịu được điều đó.
 */
function findThread(threads: ChatCustomerThread[], ref: ChatThreadRef): ChatCustomerThread | undefined {
  if (ref.threadId) return threads.find((t) => t.id === ref.threadId);
  if (ref.orderId) return threads.find((t) => t.orderId === ref.orderId);
  return threads.find((t) => !t.orderId);
}
